import { throwUa } from "./errors";
import { supabase } from "./supabase";
import { Project, ProjectStatus } from "../models/project";

export const formatMoney = (amount: number | null): string =>
  amount === null
    ? "—"
    : `${amount.toLocaleString("uk-UA", { maximumFractionDigits: 2 })} €`;

export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("uk-UA");

export const formatArea = (area: number | null): string =>
  area === null ? "—" : `${area.toLocaleString("uk-UA")} м²`;

export const formatMonthYear = (iso: string): string =>
  new Date(iso).toLocaleDateString("uk-UA", { month: "long", year: "numeric" });

const DAY_MS = 86_400_000;

export const formatRelativeDate = (iso: string): string => {
  const then = new Date(iso).getTime();
  const days = Math.floor((Date.now() - then) / DAY_MS);
  if (days <= 0) return "сьогодні";
  if (days === 1) return "вчора";
  if (days < 7) return `${days} дн. тому`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} ${w === 1 ? "тиждень" : w < 5 ? "тижні" : "тижднів"} тому`;
  }
  const m = Math.floor(days / 30);
  return `${m} ${m === 1 ? "місяць" : m < 5 ? "місяці" : "місяців"} тому`;
};

export const statusPillClass: Record<ProjectStatus, string> = {
  new: "bg-sky-500/10 text-sky-400",
  design: "bg-purple-500/10 text-purple-400",
  in_progress: "bg-brand/10 text-brand",
  quote: "bg-orange-500/10 text-orange-400",
  contract: "bg-green-500/10 text-green-400",
  completed: "bg-green-500/10 text-green-400",
};

export interface DashboardStats {
  total: number;
  inProgress: number;
  approval: number;
  completed: number;
  bonusTotal: number;
  avgBonus: number;
  contractTotal: number;
}

export const fetchOwnedProjects = async (limit?: number): Promise<Project[]> => {
  let query = supabase
    .from("projects")
    .select("*")
    .order("submitted_at", { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error } = await query;
  if (error || !data) throwUa(error);
  return (data ?? []) as Project[];
};

export const fetchOwnedProject = async (id: string): Promise<Project | null> => {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("id", id)
    .maybeSingle<Project>();
  if (error || !data) throwUa(error);
  return data;
};

export const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const { data, error } = await supabase
    .from("projects")
    .select("status, bonus_amount, contract_amount");
  if (error || !data) throwUa(error);
  const rows = (data ?? []) as Pick<
    Project,
    "status" | "bonus_amount" | "contract_amount"
  >[];
  const bonuses = rows
    .map((r) => r.bonus_amount)
    .filter((b): b is number => b !== null && b !== 0);
  return {
    total: rows.length,
    inProgress: rows.filter(
      (r) =>
        r.status === "in_progress" ||
        r.status === "new" ||
        r.status === "design"
    ).length,
    approval: rows.filter((r) => r.status === "quote").length,
    completed: rows.filter((r) => r.status === "completed").length,
    bonusTotal: rows.reduce((sum, r) => sum + (r.bonus_amount ?? 0), 0),
    avgBonus: bonuses.length
      ? bonuses.reduce((sum, b) => sum + b, 0) / bonuses.length
      : 0,
    contractTotal: rows.reduce((sum, r) => sum + (r.contract_amount ?? 0), 0),
  };
};

export interface NewProjectInput {
  name: string;
  clientName: string;
  clientPhone: string;
  address: string;
  areaM2: number;
  comment: string;
}

export const createProject = async (input: NewProjectInput): Promise<Project> => {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error("Потрібно увійти в акаунт");

  const { data, error } = await supabase
    .from("projects")
    .insert({
      owner_id: userData.user.id,
      name: input.name,
      client_name: input.clientName,
      client_phone: input.clientPhone,
      address: input.address,
      area_m2: input.areaM2,
      description: input.comment || null,
      status: "new" as ProjectStatus,
    })
    .select("*")
    .single<Project>();
  if (error || !data) throwUa(error);
  return data as Project;
};
